import {
  CMSConfiguration,
  JFieldDTO,
  jModalError
} from "@jnext/commons";
import React, {FC, Fragment, useEffect, useMemo, useState} from 'react';
import {useLocation} from 'react-router-dom';
import {useCMSStore} from 'store/cmsStore';
import {populateFieldValues, useTranslationWord} from "../../utils";
import {
  FormWrapperOnSubmit,
  GenericPageFormWrapper,
  GenericPageFormWrapperProps
} from "../../components/GenericPageForm/GenericPageFormWrapper";
import Helmet from "react-helmet";
import {FormService} from "../../service/FormService";
import {FieldDto} from "@jnext/ts-axios-formatdigitalcollection";
import {FieldDTO} from "@jnext/ts-axios-coreuser/models/field-dto";
import {FieldDTOBase} from "@jnext/ts-axios-coreuser";

// Definisce il componente FinalizationExtra come Functional Component (FC)
const FinalizationExtra: FC = () => {
  // Ottiene le pagine dal CMS store
  const { pages, setCurrentPage } = useCMSStore();
  const translateWord = useTranslationWord();

  // Definisce gli stati locali per disabilitare il form, gestire il caricamento e i messaggi di errore
  const [disableForm, setDisableForm] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(true)

  const location = useLocation();

  // Imposta i campi del form dallo stato della location o li inizializza come vuoti
  const [fields, setFields] = useState<FieldDto[]>(location.state?.fields ?? []);
  const thankyoupageCms =  useMemo(()=> location.state?.thankyoupage?.enabled ? JSON.parse(location.state?.thankyoupage) : undefined, [location.state?.thankyoupage]);
  const isNavigate = location.state?.isNavigate ?? false;

  // Effettua una chiamata API per ottenere i campi extra del profilo di finalizzazione se non è una navigazione e i campi sono vuoti
  useEffect(() => {
    (async()=> {
      if(!isNavigate && fields.length === 0){
        const response: FieldDTO[] =  await FormService.getFinalizationExtraProfile();
        setFields(response);
      }
      setLoading(false)
    })()
  }, [isNavigate]);

  // Usa useMemo per memorizzare la configurazione CMS della pagina di finalizzazione extra
  const cmsConfig: CMSConfiguration | undefined = useMemo(() => pages?.finalizationExtra, [pages?.finalizationExtra]);

  const sectionTitle = useMemo(() => cmsConfig?.section?.title, [])
  const sectionText = useMemo(() => cmsConfig?.section?.text, [])

  // Imposta la pagina corrente e aggiorna la sezione di ringraziamento se presente
  useEffect(() => {
    if(cmsConfig?.page){
      setCurrentPage(pages?.finalizationExtra ?? undefined);
    }

    if(!!cmsConfig?.section?.sections && cmsConfig?.section?.sections?.length > 0 && thankyoupageCms){
      const index = cmsConfig?.section?.sections?.findIndex((section) => section.type === 'THANKYOU_PAGE');
      cmsConfig.section.sections[index] = thankyoupageCms;
    }

  }, [cmsConfig]);

  // Funzione di submit del form che aggiorna il profilo utente e mostra la pagina di ringraziamento se necessario
  const onSubmit: FormWrapperOnSubmit = async (fieldsPayload: FieldDTOBase[], showThankYouPage) => {
    try {
      setDisableForm(true)

      await FormService.updateExtraUserFirstLevel(fieldsPayload)
          .then((res) => {
            if(res){
              showThankYouPage && showThankYouPage()
            }
          })
    } catch (error) {
      jModalError(translateWord("modalErrorTitle"), translateWord("modalErrorContent"))
      console.log('e', error)
    } finally {
      setDisableForm(false)
    }
  }

  // Configura le proprietà del form generico utilizzando useMemo
  const genericPageFormProps = useMemo(() => {
    const tmp: GenericPageFormWrapperProps = {
      cmsConfig: cmsConfig,
      context: 'CHECKOUT_WITH_CONFIRM',
      fields: populateFieldValues([...fields] as JFieldDTO[]) as JFieldDTO[],
      onSubmit,
      disableForm,
      options: cmsConfig?.section?.options,
      formProps: {
        providerProps: {
          showFieldsLabel: true
        }
      }
    };
    return tmp;
  }, [onSubmit])

  return (
      <Fragment>
        {
          // Imposta i meta tag della pagina se presenti nella configurazione CMS
          cmsConfig?.page?.options?.metaTags?.title
              ?
              <Helmet>
                <title>{cmsConfig?.page?.options?.metaTags?.title}</title>
                {cmsConfig?.page?.options?.metaTags?.description && <meta name="description" content={cmsConfig?.page?.options?.metaTags?.description} />}
              </Helmet>
              :
              <Helmet>
                <title></title>
                <meta name="description" content={''} />
              </Helmet>
        }

        {
          // Mostra il form generico o la pagina di ringraziamento in base allo stato di caricamento e alla configurazione CMS
            loading || (
                <GenericPageFormWrapper
                    {...genericPageFormProps}
                    title={sectionTitle}
                    additionalText={sectionText}
                />
            )
        }
      </Fragment>
  )
}

export default FinalizationExtra;